import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Activity } from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css";
import FeatureHeader from "../components/FeatureHeader";

export default function ProfileFull() {
  const { profile, assessments } = useAuth();
  const navigate = useNavigate();
  const assessmentsArr = Array.isArray(assessments) ? assessments : [];
  const latestAssessment = assessmentsArr.length ? assessmentsArr[0] : null;

  if (!profile)
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-light">
        <p className="text-muted fs-5">Loading profile...</p>
      </div>
    );

  const formatValue = (value) => {
    if (Array.isArray(value))
      return value.filter(Boolean).length ? value.join(", ") : "—";
    if (value === null || value === "" || value === undefined) return "—";
    if (typeof value === "object") {
      const entries = Object.entries(value).filter(([, v]) => v !== null && v !== undefined && v !== "");
      if (!entries.length) return "—";
      return entries
        .map(([k, v]) => `${k.replace(/([A-Z])/g, " $1")}: ${String(v)}`)
        .join(" | ");
    }
    return value;
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
  };

  const excludeProfileKeys = ["__v", "_id", "user", "idImage"];

  return (
    <div className="min-vh-100 bg-light ">
      <div >
        <div
          className="card shadow-lg border-0 rounded-4 mx-auto"
          style={{ maxWidth: "900px" }}
        >
         
         {/*Header */}

         <FeatureHeader title = {profile.fullName} subtitle = {profile.phone} onBack={()=>{navigate(-1)}}/>


          {/* Avatar */}
          <div style = {{display : "flex", width : "100%", justifyContent : 'center'}}>
            <img
              src={
                profile.avatar ||
                "https://via.placeholder.com/150?text=No+Avatar"
              }
              alt="Avatar"
              
              
              width={120}
              height={120}
              style={{ borderRadius : "50%", border : "solid 4px var(--primary)" }}
            />
          </div>

          {/* Profile Info */}
          <div className="card-body p-4">
            <h5 style = {{color : "var(--primary)"}}>
              Profile Information
            </h5>

            <div className="row g-3">
              {Object.entries(profile)
                .filter(([key]) => !excludeProfileKeys.includes(key))
                .map(([key, value]) => (
                  <div className="col-md-6" key={key}>
                    <div className="p-3 border rounded bg-white h-100 shadow-sm">
                      <small className="text-muted text-uppercase d-block">
                        {key.replace(/([A-Z])/g, " $1")}
                      </small>
                      <span className="fw-semibold text-dark">
                        {key === "createdAt" || key === "updatedAt"
                          ? new Date(value).toLocaleString()
                          : formatValue(value)}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Health Assessment */}
          {latestAssessment && (
            <div className="card-body border-top p-4 bg-light">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <Activity className="text-danger me-2" size={22} />
                  <h5 className="fw-semibold text-danger mb-0">
                    Health Assessment Record
                  </h5>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => navigate('/health-assessment')}
                >
                  Edit Assessment
                </button>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <small className="text-muted d-block">Age Range</small>
                    <span className="fw-semibold">
                      {formatValue(latestAssessment.ageRange || latestAssessment.age)}
                    </span>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <small className="text-muted d-block">Blood Type</small>
                    <span className="fw-semibold">
                      {formatValue(latestAssessment.bloodType)}
                    </span>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <small className="text-muted d-block">Allergy</small>
                    <span className="fw-semibold">
                      {formatValue(latestAssessment.allergy)}
                    </span>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <small className="text-muted d-block">
                      Checkup Frequency
                    </small>
                    <span className="fw-semibold">
                      {formatValue(latestAssessment.checkupFrequency)}
                    </span>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <small className="text-muted d-block">Condition</small>
                    <span className="fw-semibold">
                      {formatValue(latestAssessment.condition)}
                    </span>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <small className="text-muted d-block">Fitness Level</small>
                    <span className="fw-semibold">
                      {formatValue(latestAssessment.fitnessLevel)}
                    </span>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <small className="text-muted d-block">Sleep Level</small>
                    <span className="fw-semibold">
                      {formatValue(latestAssessment.sleepLevel)}
                    </span>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <small className="text-muted d-block">Smoke Level</small>
                    <span className="fw-semibold">
                      {formatValue(latestAssessment.smokeLevel)}
                    </span>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <small className="text-muted d-block">Goal</small>
                    <span className="fw-semibold">
                      {formatValue(latestAssessment.goal)}
                    </span>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <small className="text-muted d-block">Mood</small>
                    <span className="fw-semibold">
                      {formatValue(latestAssessment.mood)}
                    </span>
                  </div>
                </div>

                <div className="col-md-12">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <small className="text-muted d-block">Notes</small>
                    <span className="fw-semibold">
                      {formatValue(latestAssessment.notes)}
                    </span>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <small className="text-muted d-block">Created At</small>
                    <span className="fw-semibold">
                      {formatDate(latestAssessment.createdAt)}
                    </span>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <small className="text-muted d-block">Updated At</small>
                    <span className="fw-semibold">
                      {formatDate(latestAssessment.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!latestAssessment && (
            <div className="card-body border-top p-4 bg-light">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center">
                  <Activity className="text-danger me-2" size={22} />
                  <h5 className="fw-semibold text-danger mb-0">Health Assessment Record</h5>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => navigate('/health-assessment')}
                >
                  Create Assessment
                </button>
              </div>
              <p className="text-muted mb-0">No health assessment data found yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
